import React from 'react'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import { Box, Typography, Card, CardContent, Button, alpha } from '@mui/material'

export interface CleartripFormattedTransaction {
    id: string
    source: string
    transactionDate: string
    amount: number
    currency: string
    txnType?: string // 'CREDIT' or 'DEBIT'
    tripRef?: string | null
}

interface CleartripTransactionsProps {
    transactions: CleartripFormattedTransaction[]
    hasMoreTransactions: boolean
    isFetchingMore: boolean
    onLoadMore: () => void
    formatAmount: (amount: number, currency: string) => string
    transactionHistoryLabel: string
    noTransactionsLabel: string
    loadMoreLabel: string
    loadingLabel: string
    transactionIdLabel: string
    isRtl?: boolean
    showHeader?: boolean
}

const CleartripTransactions: React.FC<CleartripTransactionsProps> = ({
    transactions,
    hasMoreTransactions,
    isFetchingMore,
    onLoadMore,
    formatAmount,
    transactionHistoryLabel,
    noTransactionsLabel,
    loadMoreLabel,
    loadingLabel,
    transactionIdLabel,
    isRtl = false,
    showHeader = true
}) => {
    const componentRadius = '14px'

    return (
        <Box sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            {/* Transactions History Heading */}
            {showHeader && (
                <Box sx={{ pt: 3, pb: 1 }}>
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.5rem',
                            color: 'text.primary'
                        }}
                    >
                        {transactionHistoryLabel}
                    </Typography>
                </Box>
            )}

            {/* Transaction List */}
            {transactions.length === 0 ? (
                <Box sx={{ pt: 1.5, pb: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 400,
                            fontSize: '1rem',
                            lineHeight: '1.5rem'
                        }}
                    >
                        {noTransactionsLabel}
                    </Typography>
                </Box>
            ) : (
                <>
                    {transactions.map((txn, index) => {
                        const isCredit = txn.txnType === 'CREDIT'
                        return (
                            <Card
                                key={`${txn.id}-${index}`}
                                elevation={0}
                                sx={{
                                    mb: 1.5,
                                    bgcolor: 'background.paper',
                                    borderRadius: componentRadius,
                                    border: 1,
                                    borderColor: 'divider'
                                }}
                            >
                                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 1.75 } }}>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 48,
                                                height: 48,
                                                borderRadius: '50%',
                                                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                                                flexShrink: 0
                                            }}
                                        >
                                            <CreditCardOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '0.9375rem',
                                                    lineHeight: 1.4,
                                                    color: 'text.primary',
                                                    mb: 0.75
                                                }}
                                            >
                                                {txn.source?.trim() || '-'}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '0.8125rem',
                                                    display: 'block'
                                                }}
                                            >
                                                {txn.transactionDate}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right', flexShrink: 0, pt: 0.5 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: isCredit ? 'success.main' : 'error.main',
                                                    fontSize: '1rem',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {isCredit ? '+' : '-'}
                                                {formatAmount(txn.amount, txn.currency)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {txn.tripRef && (
                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                pt: 1.5,
                                                borderTop: 1,
                                                borderColor: 'divider',
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.disabled',
                                                    fontSize: '0.6875rem',
                                                    fontFamily: 'monospace'
                                                }}
                                            >
                                                {transactionIdLabel}: {txn.tripRef}
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        )
                    })}

                    {hasMoreTransactions && (
                        <Box sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={onLoadMore}
                                disabled={isFetchingMore}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                            >
                                {isFetchingMore ? loadingLabel : loadMoreLabel}
                            </Button>
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

export { CleartripTransactions }
export type { CleartripTransactionsProps }
